import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useAlert } from '../../context/AlertContext';
import { supabase } from '../../supabase/config';
import { accountingService } from '../../services/accountingService';
import { inventoryService } from '../../services/inventoryService';
import { useCustomers } from '../../context/CustomerContext';
import PremiumDatePicker from '../../components/PremiumDatePicker';
import {
    TrendingUp, DollarSign, Package, RefreshCw, ArrowDownRight, Percent,
    Plus, X, Calculator, ShoppingBag, Info, Search, ChevronLeft, ChevronRight,
    Trash2, Calendar, BarChart3, PieChart, Wallet, Receipt, FileText,
    ArrowUpRight, AlertTriangle, Banknote
} from 'lucide-react';
import {
    Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement,
    BarElement, Title, Tooltip, Legend, ArcElement, Filler
} from 'chart.js';
import { Line, Doughnut, Bar } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Title, Tooltip, Legend, Filler);

const SALES_PER_PAGE = 20;
const EXPENSE_CATEGORIES = {
    personnel: { label: 'Personel', color: '#8b5cf6', icon: '👤' },
    rent: { label: 'Kira', color: '#f59e0b', icon: '🏠' },
    utility: { label: 'Fatura', color: '#0ea5e9', icon: '💡' },
    supplies: { label: 'Malzeme', color: '#10b981', icon: '📦' },
    marketing: { label: 'Pazarlama', color: '#ec4899', icon: '📣' },
    maintenance: { label: 'Bakım', color: '#6366f1', icon: '🔧' },
    other: { label: 'Diğer', color: '#64748b', icon: '📋' },
};

const Accounting = () => {
    const { t } = useTranslation();
    const { effectiveTenantId } = useAuth();
    const { showAlert, showConfirm } = useAlert();
    const { customers } = useCustomers();
    const [activeTab, setActiveTab] = useState('income');
    const [loading, setLoading] = useState(true);
    const [salesData, setSalesData] = useState([]);
    const [manualExpenses, setManualExpenses] = useState([]);
    const [stocks, setStocks] = useState({});
    const [products, setProducts] = useState([]);
    const [localCosts, setLocalCosts] = useState({});
    const [saving, setSaving] = useState(false);
    const [isExpenseModalOpen, setIsExpenseModalOpen] = useState(false);
    const [newExpense, setNewExpense] = useState({ title: '', category: 'personnel', amount: '', date: new Date().toISOString().split('T')[0] });
    const [dateRange, setDateRange] = useState({ start: '', end: '' });
    const [quickRange, setQuickRange] = useState('all');

    useEffect(() => {
        if (!effectiveTenantId) return;
        let isCancelled = false;
        let unsubs = [];

        const init = async () => {
            setLoading(true);
            try {
                const [{ data: mDoc }, { data: cDoc }] = await Promise.all([
                    supabase.from('menus').select('*').eq('tenant_id', effectiveTenantId).maybeSingle(),
                    supabase.from('product_costs').select('*').eq('tenant_id', effectiveTenantId).maybeSingle()
                ]);
                if (isCancelled) return;

                const costs = cDoc ? (cDoc.costs || {}) : {};
                setLocalCosts(costs);

                const flattened = [];
                const seenIds = new Set();
                if (mDoc) {
                    const categoriesJsonField = mDoc.categories_json || mDoc.categoriesJson;
                    let rawCategories = categoriesJsonField ? JSON.parse(categoriesJsonField) : (mDoc.categories || []);
                    const cats = Array.isArray(rawCategories) ? rawCategories : Object.values(rawCategories);
                    cats.forEach(cat => {
                        const items = Array.isArray(cat.items) ? cat.items : Object.values(cat.items || {});
                        items.forEach(item => {
                            const baseId = `${cat.categoryName}_${item.name}`;
                            let uniqueId = baseId;
                            let counter = 1;
                            while (seenIds.has(uniqueId)) { uniqueId = `${baseId}_${counter}`; counter++; }
                            seenIds.add(uniqueId);
                            const priceStr = (item.price || "0").toString().replace(/[^\d.,]/g, '').replace(',', '.');
                            flattened.push({
                                id: uniqueId, name: item.name, category: cat.categoryName,
                                image: item.image || item.imageUrl || null,
                                numericPrice: parseFloat(priceStr) || 0,
                                cost: costs[uniqueId] || 0
                            });
                        });
                    });
                }
                setProducts(flattened);
                unsubs.push(accountingService.subscribeToSales(effectiveTenantId, setSalesData));
                unsubs.push(accountingService.subscribeToExpenses(effectiveTenantId, setManualExpenses));
                unsubs.push(inventoryService.subscribeToStocks(effectiveTenantId, setStocks));
                setLoading(false);
            } catch (err) {
                console.error("Accounting Init Error:", err);
                setLoading(false);
            }
        };
        init();
        return () => { isCancelled = true; unsubs.forEach(u => u && u()); };
    }, [effectiveTenantId]);

    // Quick date presets
    const applyQuickRange = (key) => {
        setQuickRange(key);
        const now = new Date();
        const today = now.toISOString().split('T')[0];
        switch (key) {
            case 'today': setDateRange({ start: today, end: today }); break;
            case 'week': {
                const d = new Date(now);
                d.setDate(d.getDate() - d.getDay() + (d.getDay() === 0 ? -6 : 1));
                setDateRange({ start: d.toISOString().split('T')[0], end: today });
                break;
            }
            case 'month': {
                const d = new Date(now.getFullYear(), now.getMonth(), 1);
                setDateRange({ start: d.toISOString().split('T')[0], end: today });
                break;
            }
            case 'quarter': {
                const d = new Date(now);
                d.setMonth(d.getMonth() - 3);
                setDateRange({ start: d.toISOString().split('T')[0], end: today });
                break;
            }
            case 'year': {
                const d = new Date(now.getFullYear(), 0, 1);
                setDateRange({ start: d.toISOString().split('T')[0], end: today });
                break;
            }
            default: setDateRange({ start: '', end: '' }); break;
        }
    };

    const filteredSalesData = useMemo(() => {
        let filtered = salesData;
        if (dateRange.start) filtered = filtered.filter(sale => new Date(sale.timestamp) >= new Date(dateRange.start));
        if (dateRange.end) {
            const endDate = new Date(dateRange.end);
            endDate.setHours(23, 59, 59, 999);
            filtered = filtered.filter(sale => new Date(sale.timestamp) <= endDate);
        }
        return filtered.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    }, [salesData, dateRange]);

    const filteredManualExpenses = useMemo(() => {
        let filtered = manualExpenses;
        if (dateRange.start) filtered = filtered.filter(exp => new Date(exp.date) >= new Date(dateRange.start));
        if (dateRange.end) {
            const endDate = new Date(dateRange.end);
            endDate.setHours(23, 59, 59, 999);
            filtered = filtered.filter(exp => new Date(exp.date) <= endDate);
        }
        return filtered.sort((a, b) => new Date(b.date) - new Date(a.date));
    }, [manualExpenses, dateRange]);

    const stats = useMemo(() => {
        const revenue = filteredSalesData.reduce((acc, curr) => acc + (curr.amount || 0), 0);
        const totalManualExpenses = filteredManualExpenses.reduce((acc, curr) => acc + (parseFloat(curr.amount) || 0), 0);
        let productTotalStockValue = 0;
        const breakdown = products.map(p => {
            const stockQty = stocks[p.id] || 0;
            const unitCost = localCosts[p.id] || 0;
            const total = stockQty * unitCost;
            productTotalStockValue += total;
            return { ...p, total, qty: stockQty };
        }).filter(b => b.total > 0);

        let periodCogs = 0;
        filteredSalesData.forEach(sale => {
            (sale.items || []).forEach(item => {
                const product = products.find(p => p.name === item.name);
                const cost = product ? (localCosts[product.id] || 0) : 0;
                periodCogs += cost * (item.quantity || 1);
            });
        });

        const totalGider = periodCogs + totalManualExpenses;
        const orderCount = filteredSalesData.length;
        const avgOrder = orderCount > 0 ? revenue / orderCount : 0;

        return {
            totalRevenue: revenue, productTotalStockValue, totalManualExpenses,
            totalGider, grossProductProfit: revenue - periodCogs,
            profit: revenue - totalGider, productBreakdown: breakdown,
            orderCount, avgOrder, periodCogs,
        };
    }, [filteredSalesData, products, filteredManualExpenses, stocks, localCosts]);

    const handleAddExpense = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await accountingService.addExpense(newExpense, effectiveTenantId);
            setIsExpenseModalOpen(false);
            setNewExpense({ title: '', category: 'personnel', amount: '', date: new Date().toISOString().split('T')[0] });
        } catch { showAlert("Hata oluştu.", "error"); }
        finally { setSaving(false); }
    };

    const handleDeleteExpense = async (id) => {
        if (!await showConfirm('Bu gideri silmek istediğinize emin misiniz?', { title: 'Gideri Sil', confirmText: 'Sil', type: 'warning' })) return;
        try {
            const { error } = await supabase.from('accounting_expenses').delete().eq('id', id);
            if (error) throw error;
        } catch { showAlert('Silme hatası.', 'error'); }
    };

    const handleSaveCosts = async () => {
        const confirmed = await showConfirm('Ürün maliyetlerini kaydetmek istediğinize emin misiniz?', {
            title: 'Maliyetleri Kaydet',
            confirmText: 'Kaydet',
            type: 'info'
        });
        if (!confirmed) return;

        setSaving(true);
        try {
            await accountingService.saveProductCosts(effectiveTenantId, localCosts);
            showAlert('Ürün maliyetleri kaydedildi.', 'success');
        } catch { showAlert('Hata oluştu.', 'error'); }
        finally { setSaving(false); }
    };

    const tabs = [
        { key: 'income', label: 'Gelir / Satış', icon: TrendingUp },
        { key: 'expenses', label: 'Giderler', icon: ArrowDownRight },
        { key: 'products', label: 'Ürün Maliyeti', icon: Package },
    ];

    if (loading) return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', flexDirection: 'column', gap: '16px' }}>
            <RefreshCw className="animate-spin" size={32} color="var(--primary)" />
            <span style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>Muhasebe verileri yükleniyor...</span>
        </div>
    );

    const quickRanges = [
        { key: 'all', label: 'Tümü' },
        { key: 'today', label: 'Bugün' },
        { key: 'week', label: 'Bu Hafta' },
        { key: 'month', label: 'Bu Ay' },
        { key: 'quarter', label: 'Son 3 Ay' },
        { key: 'year', label: 'Bu Yıl' },
    ];

    return (
        <div className="fade-in">
            {/* Header */}
            <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
                <div>
                    <h1 style={{ fontWeight: 900, fontSize: '2.25rem', letterSpacing: '-0.02em', marginBottom: '0.5rem' }}>Muhasebe Paneli</h1>
                    <p style={{ color: 'var(--text-muted)', fontWeight: 600 }}>Gelir, gider ve kâr marjlarını analiz edin.</p>
                </div>
                {activeTab === 'expenses' && (
                    <button onClick={() => setIsExpenseModalOpen(true)} className="btn-primary" style={{
                        display: 'flex', alignItems: 'center', gap: '8px',
                        padding: '10px 20px', borderRadius: '12px', fontWeight: 700, fontSize: '0.9rem',
                    }}>
                        <Plus size={18} /> Gider Ekle
                    </button>
                )}
            </div>

            {/* Tabs + Filters */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
                <div style={{ display: 'flex', gap: '0.5rem', background: 'rgba(var(--primary-rgb), 0.05)', padding: '0.5rem', borderRadius: '16px', width: 'fit-content', maxWidth: '100%' }}>
                    {tabs.map(tab => {
                        const TabIcon = tab.icon;
                        return (
                            <button
                                key={tab.key}
                                className="tab-btn"
                                onClick={() => setActiveTab(tab.key)}
                                style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    gap: '8px', padding: '10px 24px', borderRadius: '12px', border: 'none',
                                    background: activeTab === tab.key ? 'var(--primary)' : 'transparent',
                                    color: activeTab === tab.key ? 'var(--text-on-primary)' : 'var(--text-main)',
                                    fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                <span style={{ display: 'flex', alignItems: 'center' }}><TabIcon size={16} /></span>
                                {tab.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Date Filter - for income & expenses */}
            {(activeTab === 'income' || activeTab === 'expenses') && (
                <div style={{
                    display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap',
                }}>
                    {/* Quick presets */}
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        {quickRanges.map(r => (
                            <button
                                key={r.key}
                                onClick={() => applyQuickRange(r.key)}
                                style={{
                                    padding: '6px 14px', borderRadius: '8px', fontSize: '0.78rem',
                                    fontWeight: quickRange === r.key ? 700 : 500,
                                    border: quickRange === r.key ? '1px solid var(--primary)' : '1px solid var(--border)',
                                    background: quickRange === r.key ? 'rgba(var(--primary-rgb), 0.08)' : 'var(--bg-card)',
                                    color: quickRange === r.key ? 'var(--primary)' : 'var(--text-main)',
                                    cursor: 'pointer', transition: 'all 0.15s',
                                }}
                            >
                                {r.label}
                            </button>
                        ))}
                    </div>

                    <div style={{ width: '1px', height: '24px', background: 'var(--border)', margin: '0 4px' }} />

                    {/* Custom date range */}
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
                        <div style={{ minWidth: '160px' }}>
                            <PremiumDatePicker
                                value={dateRange.start}
                                onChange={dateStr => { setDateRange(prev => ({ ...prev, start: dateStr })); setQuickRange('custom'); }}
                            />
                        </div>
                        <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem', fontWeight: 600, paddingBottom: '12px' }}>→</span>
                        <div style={{ minWidth: '160px' }}>
                            <PremiumDatePicker
                                value={dateRange.end}
                                onChange={dateStr => { setDateRange(prev => ({ ...prev, end: dateStr })); setQuickRange('custom'); }}
                                align="right"
                            />
                        </div>
                        {(dateRange.start || dateRange.end) && (
                            <button
                                onClick={() => { setDateRange({ start: '', end: '' }); setQuickRange('all'); }}
                                style={{
                                    padding: '10px', background: 'rgba(239, 68, 68, 0.08)',
                                    color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.2)',
                                    borderRadius: '10px', cursor: 'pointer', display: 'flex',
                                    alignItems: 'center', justifyContent: 'center',
                                }}
                                title="Filtreyi Temizle"
                            >
                                <X size={16} />
                            </button>
                        )}
                    </div>
                </div>
            )}

            {activeTab === 'income' && <IncomeTab stats={stats} salesData={filteredSalesData} products={products} customers={customers} localCosts={localCosts} />}
            {activeTab === 'expenses' && <ExpensesTab stats={stats} manualExpenses={filteredManualExpenses} onAddOpen={() => setIsExpenseModalOpen(true)} onDelete={handleDeleteExpense} />}
            {activeTab === 'products' && <ProductsTab products={products} localCosts={localCosts} setLocalCosts={setLocalCosts} onSave={handleSaveCosts} saving={saving} />}

            {/* Expense Modal */}
            {isExpenseModalOpen && (
                <div className="profile-modal-overlay" onClick={() => setIsExpenseModalOpen(false)} style={{ zIndex: 9999 }}>
                    <div className="card glass shadow-2xl" style={{
                        maxWidth: '480px', width: '100%', padding: 0,
                        overflow: 'hidden', borderRadius: '24px', border: 'none',
                    }} onClick={e => e.stopPropagation()}>
                        {/* Modal Header */}
                        <div style={{
                            padding: '1.5rem 2rem', borderBottom: '1px solid var(--border)',
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            background: 'linear-gradient(to bottom, rgba(239, 68, 68, 0.03), transparent)',
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div style={{
                                    width: '42px', height: '42px', borderRadius: '12px',
                                    background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <Receipt size={20} />
                                </div>
                                <div>
                                    <h2 style={{ fontWeight: 800, fontSize: '1.1rem', margin: 0 }}>Yeni Gider Ekle</h2>
                                    <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: 0 }}>Operasyonel gider kaydı oluşturun</p>
                                </div>
                            </div>
                            <button onClick={() => setIsExpenseModalOpen(false)} style={{
                                background: 'rgba(0,0,0,0.05)', border: 'none', borderRadius: '50%',
                                width: '32px', height: '32px', cursor: 'pointer', color: 'var(--text-main)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <X size={16} />
                            </button>
                        </div>

                        {/* Modal Form */}
                        <form onSubmit={handleAddExpense} style={{ padding: '1.5rem 2rem' }}>
                            <div style={{ marginBottom: '1.25rem' }}>
                                <label style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-muted)', display: 'block', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Gider Başlığı</label>
                                <div style={{ position: 'relative' }}>
                                    <FileText size={16} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                                    <input
                                        type="text" placeholder="Örn: Personel Maaşları"
                                        value={newExpense.title} required
                                        onChange={e => setNewExpense({ ...newExpense, title: e.target.value })}
                                        style={{
                                            width: '100%', height: '46px', paddingLeft: '42px',
                                            background: 'var(--bg-main)', border: '1px solid var(--border)',
                                            borderRadius: '12px', fontSize: '0.9rem', outline: 'none',
                                            color: 'var(--text-main)',
                                        }}
                                    />
                                </div>
                            </div>

                            <div style={{ marginBottom: '1.25rem' }}>
                                <label style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-muted)', display: 'block', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Kategori</label>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '6px' }}>
                                    {Object.entries(EXPENSE_CATEGORIES).map(([key, cat]) => (
                                        <button
                                            key={key} type="button"
                                            onClick={() => setNewExpense({ ...newExpense, category: key })}
                                            style={{
                                                padding: '8px 10px', borderRadius: '10px', fontSize: '0.78rem',
                                                fontWeight: newExpense.category === key ? 700 : 500,
                                                border: newExpense.category === key ? `2px solid ${cat.color}` : '2px solid var(--border)',
                                                background: newExpense.category === key ? `${cat.color}12` : 'var(--bg-card)',
                                                color: newExpense.category === key ? cat.color : 'var(--text-main)',
                                                cursor: 'pointer', transition: 'all 0.15s',
                                                display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'center',
                                            }}
                                        >
                                            <span>{cat.icon}</span> {cat.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="accounting-date-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '2rem' }}>
                                <div>
                                    <label style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-muted)', display: 'block', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Tutar (₺)</label>
                                    <div style={{ position: 'relative' }}>
                                        <Banknote size={16} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                                        <input
                                            type="number" placeholder="0.00"
                                            value={newExpense.amount} required
                                            onChange={e => setNewExpense({ ...newExpense, amount: e.target.value })}
                                            style={{
                                                width: '100%', height: '46px', paddingLeft: '42px',
                                                background: 'var(--bg-main)', border: '1px solid var(--border)',
                                                borderRadius: '12px', fontSize: '0.9rem', outline: 'none',
                                                color: 'var(--text-main)',
                                            }}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-muted)', display: 'block', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Tarih</label>
                                    <div style={{ position: 'relative' }}>
                                        <Calendar size={16} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                                        <input
                                            type="date" value={newExpense.date} required
                                            onChange={e => setNewExpense({ ...newExpense, date: e.target.value })}
                                            style={{
                                                width: '100%', height: '46px', paddingLeft: '42px',
                                                background: 'var(--bg-main)', border: '1px solid var(--border)',
                                                borderRadius: '12px', fontSize: '0.9rem', outline: 'none',
                                                color: 'var(--text-main)',
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div style={{ display: 'flex', gap: '0.75rem' }}>
                                <button type="button" onClick={() => setIsExpenseModalOpen(false)} className="btn-secondary" style={{
                                    flex: 1, height: '48px', borderRadius: '12px', fontWeight: 700,
                                }}>İptal</button>
                                <button type="submit" disabled={saving} className="btn-primary" style={{
                                    flex: 1, height: '48px', borderRadius: '12px', fontWeight: 700,
                                    opacity: saving ? 0.7 : 1,
                                }}>
                                    {saving ? 'Kaydediliyor...' : 'Gider Ekle'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

/* ===== INCOME TAB ===== */
const IncomeTab = ({ stats, salesData, products, customers, localCosts }) => {
    const [salesPage, setSalesPage] = useState(1);

    const getCustomerDisplayName = (sale) => {
        if (!sale.customerId) return sale.customerName || 'Misafir';
        const customer = (customers || []).find(c => c.id === sale.customerId);
        if (customer) {
            const fullName = `${customer.firstName || ''} ${customer.lastName || ''}`.trim();
            if (fullName) return fullName;
        }
        return sale.customerName || 'Misafir';
    };

    const chartData = useMemo(() => {
        if (!salesData || salesData.length === 0) {
            return { labels: [], datasets: [{ label: 'Ciro', data: [], borderColor: '#10b981', backgroundColor: 'rgba(16, 185, 129, 0.08)', fill: true, tension: 0.4 }] };
        }
        const times = salesData.map(s => new Date(s.timestamp).getTime());
        const diffDays = (Math.max(...times) - Math.min(...times)) / (1000 * 3600 * 24);
        let grouped = {};
        salesData.forEach(sale => {
            const date = new Date(sale.timestamp);
            let key = diffDays <= 1 ? `${date.getHours().toString().padStart(2, '0')}:00` : date.toLocaleDateString('tr-TR');
            grouped[key] = (grouped[key] || 0) + (sale.amount || 0);
        });
        const sortedKeys = Object.keys(grouped).sort((a, b) => {
            if (diffDays <= 1) return parseInt(a) - parseInt(b);
            const [d1, m1, y1] = a.split('.'); const [d2, m2, y2] = b.split('.');
            return new Date(`${y1}-${m1}-${d1}`) - new Date(`${y2}-${m2}-${d2}`);
        });
        return {
            labels: sortedKeys,
            datasets: [{
                label: 'Ciro (₺)', data: sortedKeys.map(k => grouped[k]),
                borderColor: '#10b981', backgroundColor: 'rgba(16, 185, 129, 0.08)',
                fill: true, tension: 0.4, pointRadius: sortedKeys.length > 30 ? 0 : 4,
                pointBackgroundColor: '#10b981', borderWidth: 2,
            }]
        };
    }, [salesData]);

    const productSalesData = useMemo(() => {
        const productCounts = {};
        salesData.forEach(sale => {
            (sale.items || []).forEach(item => {
                productCounts[item.name] = (productCounts[item.name] || 0) + (item.quantity || 1);
            });
        });
        const sorted = Object.entries(productCounts).sort(([, a], [, b]) => b - a).slice(0, 6);
        const colors = ['#10b981', '#3b82f6', '#f59e0b', '#8b5cf6', '#ec4899', '#ef4444'];
        return {
            labels: sorted.map(([name]) => name),
            datasets: [{ data: sorted.map(([, count]) => count), backgroundColor: colors.slice(0, sorted.length), borderWidth: 0 }]
        };
    }, [salesData]);

    const totalSalesPages = Math.ceil(salesData.length / SALES_PER_PAGE);
    const paginatedSales = salesData.slice((salesPage - 1) * SALES_PER_PAGE, salesPage * SALES_PER_PAGE);

    return (
        <div className="fade-in">
            {/* Metric Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                <MetricCard title="Toplam Ciro" value={`${stats.totalRevenue.toLocaleString('tr-TR')} ₺`} color="#10b981" icon={<TrendingUp size={20} />} trend={stats.totalRevenue > 0 ? 'up' : null} />
                <MetricCard title="Sipariş Sayısı" value={stats.orderCount.toLocaleString()} color="#3b82f6" icon={<ShoppingBag size={20} />} sub={`Ort: ${stats.avgOrder.toLocaleString('tr-TR', { maximumFractionDigits: 0 })} ₺`} />
                <MetricCard title="Brüt Kâr" value={`${stats.grossProductProfit.toLocaleString('tr-TR')} ₺`} color="#f59e0b" icon={<Wallet size={20} />} />
                <MetricCard title="Net Kâr" value={`${stats.profit.toLocaleString('tr-TR')} ₺`} color={stats.profit >= 0 ? '#0ea5e9' : '#ef4444'} icon={<DollarSign size={20} />} />
                <MetricCard title="Kâr Marjı" value={`%${stats.totalRevenue > 0 ? Math.floor((stats.profit / stats.totalRevenue) * 100) : 0}`} color="#8b5cf6" icon={<Percent size={20} />} />
            </div>

            {/* Charts */}
            <div className="accounting-charts-grid" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                <div className="card glass" style={{ padding: '1.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '1.25rem' }}>
                        <BarChart3 size={18} style={{ color: 'var(--primary)' }} />
                        <h3 style={{ fontWeight: 700, fontSize: '0.95rem', margin: 0 }}>Satış Grafiği</h3>
                    </div>
                    <div style={{ height: '280px' }}>
                        {chartData.labels.length > 0 ? (
                            <Line data={chartData} options={{
                                responsive: true, maintainAspectRatio: false,
                                plugins: { legend: { display: false }, tooltip: { callbacks: { label: (ctx) => `${ctx.parsed.y.toLocaleString('tr-TR')} ₺` } } },
                                scales: {
                                    y: { beginAtZero: true, grid: { color: 'rgba(0,0,0,0.04)' }, ticks: { callback: v => `${v.toLocaleString()} ₺` } },
                                    x: { grid: { display: false } }
                                },
                            }} />
                        ) : (
                            <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', flexDirection: 'column', gap: '8px' }}>
                                <BarChart3 size={32} style={{ opacity: 0.2 }} />
                                <span style={{ fontSize: '0.85rem' }}>Belirtilen tarihte veri yok</span>
                            </div>
                        )}
                    </div>
                </div>

                <div className="card glass" style={{ padding: '1.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '1.25rem' }}>
                        <PieChart size={18} style={{ color: 'var(--primary)' }} />
                        <h3 style={{ fontWeight: 700, fontSize: '0.95rem', margin: 0 }}>En Çok Satanlar</h3>
                    </div>
                    <div style={{ height: '280px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {productSalesData.labels.length > 0 ? (
                            <Doughnut data={productSalesData} options={{
                                responsive: true, maintainAspectRatio: false,
                                plugins: { legend: { position: 'bottom', labels: { boxWidth: 10, padding: 12, font: { size: 11, weight: 'bold' } } } },
                                cutout: '65%',
                            }} />
                        ) : (
                            <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', textAlign: 'center' }}>
                                <PieChart size={32} style={{ opacity: 0.2, marginBottom: '8px' }} /><br />Veri yok
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Sales Table */}
            <div className="card glass" style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{ fontWeight: 700, fontSize: '0.95rem', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Receipt size={16} style={{ color: 'var(--primary)' }} /> Satış Geçmişi
                    </h3>
                    <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{salesData.length} işlem</span>
                </div>
                <div className="accounting-table-wrapper" style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
                        <thead>
                            <tr style={{ borderBottom: '2px solid var(--border)' }}>
                                <th style={thStyle}>Ürün / İçerik</th>
                                <th style={thStyle}>Müşteri</th>
                                <th style={{ ...thStyle, textAlign: 'center' }}>Adet</th>
                                <th style={{ ...thStyle, textAlign: 'right' }}>Tutar</th>
                                <th style={{ ...thStyle, textAlign: 'right' }}>Tarih</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedSales.length === 0 ? (
                                <tr><td colSpan="5" style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                                    <ShoppingBag size={32} style={{ opacity: 0.2, marginBottom: '8px' }} /><br />Henüz satış bulunmuyor
                                </td></tr>
                            ) : paginatedSales.map(sale => (
                                <tr key={sale.id} style={{ borderBottom: '1px solid var(--border)', transition: 'background 0.15s' }}
                                    onMouseOver={e => e.currentTarget.style.background = 'rgba(var(--primary-rgb), 0.02)'}
                                    onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                                >
                                    <td style={{ padding: '0.85rem 1.5rem' }}>
                                        <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{sale.items?.map(i => i.name).join(', ') || 'Hizmet/Ürün'}</div>
                                        {sale.items?.length > 0 && (
                                            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                                                {sale.items.map(i => `${i.name} x${i.quantity || 1}`).join(' · ')}
                                            </div>
                                        )}
                                    </td>
                                    <td style={{ padding: '0.85rem 1rem', fontSize: '0.85rem' }}>{getCustomerDisplayName(sale)}</td>
                                    <td style={{ padding: '0.85rem 1rem', textAlign: 'center', fontWeight: 600 }}>{sale.quantity}</td>
                                    <td style={{ padding: '0.85rem 1rem', textAlign: 'right', fontWeight: 800, color: 'var(--primary)' }}>{sale.amount?.toLocaleString('tr-TR')} ₺</td>
                                    <td style={{ padding: '0.85rem 1rem', textAlign: 'right', color: 'var(--text-muted)', fontSize: '0.78rem' }}>
                                        {new Date(sale.timestamp).toLocaleDateString('tr-TR')}
                                        <div style={{ fontSize: '0.68rem' }}>{new Date(sale.timestamp).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {totalSalesPages > 1 && (
                    <div style={{
                        padding: '0.75rem 1.5rem', borderTop: '1px solid var(--border)',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        background: 'var(--bg-main)',
                    }}>
                        <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                            {((salesPage - 1) * SALES_PER_PAGE) + 1}–{Math.min(salesPage * SALES_PER_PAGE, salesData.length)} / {salesData.length}
                        </span>
                        <div style={{ display: 'flex', gap: '4px' }}>
                            <button onClick={() => setSalesPage(p => Math.max(1, p - 1))} disabled={salesPage === 1}
                                style={{ ...paginationBtnStyle, opacity: salesPage === 1 ? 0.4 : 1 }}><ChevronLeft size={16} /></button>
                            <button onClick={() => setSalesPage(p => Math.min(totalSalesPages, p + 1))} disabled={salesPage === totalSalesPages}
                                style={{ ...paginationBtnStyle, opacity: salesPage === totalSalesPages ? 0.4 : 1 }}><ChevronRight size={16} /></button>
                        </div>
                    </div>
                )}
            </div>

            <style>{`
                @media (max-width: 1024px) { .accounting-charts-grid { grid-template-columns: 1fr !important; } }
                @media (max-width: 640px) {
                    .accounting-date-grid { grid-template-columns: 1fr !important; }
                    .accounting-table-wrapper { overflow-x: auto; }
                }
            `}</style>
        </div>
    );
};

/* ===== EXPENSES TAB ===== */
const ExpensesTab = ({ stats, manualExpenses, onAddOpen, onDelete }) => {
    const expensesByCategory = useMemo(() => {
        const map = {};
        manualExpenses.forEach(exp => {
            const cat = exp.category || 'other';
            map[cat] = (map[cat] || 0) + (parseFloat(exp.amount) || 0);
        });
        return map;
    }, [manualExpenses]);

    const expenseChartData = useMemo(() => {
        const entries = Object.entries(expensesByCategory).sort(([, a], [, b]) => b - a);
        return {
            labels: entries.map(([k]) => EXPENSE_CATEGORIES[k]?.label || k),
            datasets: [{
                data: entries.map(([, v]) => v),
                backgroundColor: entries.map(([k]) => EXPENSE_CATEGORIES[k]?.color || '#64748b'),
                borderWidth: 0,
            }]
        };
    }, [expensesByCategory]);

    return (
        <div className="fade-in">
            {/* Metric Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                <MetricCard title="Toplam Gider" value={`${stats.totalGider.toLocaleString('tr-TR')} ₺`} color="#ef4444" icon={<ArrowDownRight size={20} />} />
                <MetricCard title="SMM (Satılan Mal)" value={`${stats.periodCogs.toLocaleString('tr-TR')} ₺`} color="#f59e0b" icon={<Package size={20} />} sub="Satış bazlı maliyet" />
                <MetricCard title="Stok Değeri" value={`${(stats.productTotalStockValue || 0).toLocaleString('tr-TR')} ₺`} color="#0ea5e9" icon={<ShoppingBag size={20} />} sub="Mevcut stok maliyeti" />
                <MetricCard title="Operasyonel" value={`${stats.totalManualExpenses.toLocaleString('tr-TR')} ₺`} color="#8b5cf6" icon={<Calculator size={20} />} sub={`${manualExpenses.length} kayıt`} />
            </div>

            {/* Expense chart + table */}
            <div className="expense-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '1rem' }}>
                {/* Category breakdown */}
                <div className="card glass" style={{ padding: '1.5rem' }}>
                    <h3 style={{ fontWeight: 700, fontSize: '0.95rem', margin: '0 0 1.25rem 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <PieChart size={16} style={{ color: 'var(--primary)' }} /> Kategori Dağılımı
                    </h3>
                    {expenseChartData.labels.length > 0 ? (
                        <>
                            <div style={{ height: '200px', marginBottom: '1rem' }}>
                                <Doughnut data={expenseChartData} options={{
                                    responsive: true, maintainAspectRatio: false,
                                    plugins: { legend: { display: false } }, cutout: '60%',
                                }} />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {Object.entries(expensesByCategory).sort(([, a], [, b]) => b - a).map(([key, amount]) => {
                                    const cat = EXPENSE_CATEGORIES[key] || EXPENSE_CATEGORIES.other;
                                    const pct = stats.totalManualExpenses > 0 ? Math.round((amount / stats.totalManualExpenses) * 100) : 0;
                                    return (
                                        <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: cat.color, flexShrink: 0 }} />
                                            <span style={{ fontSize: '0.8rem', flex: 1 }}>{cat.label}</span>
                                            <span style={{ fontSize: '0.78rem', fontWeight: 700 }}>{amount.toLocaleString('tr-TR')} ₺</span>
                                            <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', width: '36px', textAlign: 'right' }}>%{pct}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    ) : (
                        <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                            <Receipt size={32} style={{ opacity: 0.2, marginBottom: '8px' }} /><br />Gider kaydı yok
                        </div>
                    )}
                </div>

                {/* Expense table */}
                <div className="card glass" style={{ padding: 0, overflow: 'hidden' }}>
                    <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h3 style={{ fontWeight: 700, fontSize: '0.95rem', margin: 0 }}>Gider Listesi</h3>
                        <button onClick={onAddOpen} style={{
                            display: 'flex', alignItems: 'center', gap: '6px',
                            padding: '6px 14px', borderRadius: '8px', fontSize: '0.78rem',
                            fontWeight: 600, border: '1px solid var(--primary)',
                            background: 'rgba(var(--primary-rgb), 0.05)', color: 'var(--primary)',
                            cursor: 'pointer',
                        }}>
                            <Plus size={14} /> Ekle
                        </button>
                    </div>
                    <div style={{ overflowX: 'auto', maxHeight: '500px', overflowY: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ borderBottom: '2px solid var(--border)', position: 'sticky', top: 0, background: 'var(--bg-card)', zIndex: 5 }}>
                                    <th style={thStyle}>Gider Kalemi</th>
                                    <th style={thStyle}>Kategori</th>
                                    <th style={thStyle}>Tarih</th>
                                    <th style={{ ...thStyle, textAlign: 'right' }}>Tutar</th>
                                    <th style={{ ...thStyle, textAlign: 'center', width: '50px' }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {/* Stock costs */}
                                {stats.productBreakdown.map(item => (
                                    <tr key={`cost-${item.id}`} style={{ borderBottom: '1px solid var(--border)', background: 'rgba(var(--primary-rgb), 0.01)' }}>
                                        <td style={{ padding: '0.85rem 1.5rem' }}>
                                            <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{item.name}</div>
                                            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{item.qty} adet stokta</div>
                                        </td>
                                        <td style={{ padding: '0.85rem 1rem' }}>
                                            <span style={{
                                                fontSize: '0.7rem', fontWeight: 700, padding: '3px 8px',
                                                borderRadius: '6px', background: 'rgba(239, 68, 68, 0.08)',
                                                color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.15)',
                                            }}>STOK</span>
                                        </td>
                                        <td style={{ padding: '0.85rem 1rem', fontSize: '0.8rem', color: 'var(--text-muted)' }}>Otomatik</td>
                                        <td style={{ padding: '0.85rem 1rem', textAlign: 'right', fontWeight: 700 }}>{item.total.toLocaleString('tr-TR')} ₺</td>
                                        <td></td>
                                    </tr>
                                ))}
                                {/* Manual expenses */}
                                {manualExpenses.map(exp => {
                                    const cat = EXPENSE_CATEGORIES[exp.category] || EXPENSE_CATEGORIES.other;
                                    return (
                                        <tr key={exp.id} style={{ borderBottom: '1px solid var(--border)' }}
                                            onMouseOver={e => e.currentTarget.style.background = 'rgba(var(--primary-rgb), 0.02)'}
                                            onMouseOut={e => e.currentTarget.style.background = 'transparent'}>
                                            <td style={{ padding: '0.85rem 1.5rem', fontWeight: 600, fontSize: '0.85rem' }}>{exp.title}</td>
                                            <td style={{ padding: '0.85rem 1rem' }}>
                                                <span style={{
                                                    fontSize: '0.7rem', fontWeight: 700, padding: '3px 8px',
                                                    borderRadius: '6px', background: `${cat.color}12`,
                                                    color: cat.color, border: `1px solid ${cat.color}25`,
                                                }}>{cat.icon} {cat.label}</span>
                                            </td>
                                            <td style={{ padding: '0.85rem 1rem', fontSize: '0.8rem', color: 'var(--text-muted)' }}>{new Date(exp.date).toLocaleDateString('tr-TR')}</td>
                                            <td style={{ padding: '0.85rem 1rem', textAlign: 'right', fontWeight: 700 }}>{parseFloat(exp.amount || 0).toLocaleString('tr-TR')} ₺</td>
                                            <td style={{ padding: '0.85rem 0.5rem', textAlign: 'center' }}>
                                                <button onClick={() => onDelete(exp.id)} style={{
                                                    background: 'none', border: 'none', cursor: 'pointer',
                                                    color: 'var(--text-muted)', padding: '4px', borderRadius: '6px',
                                                    transition: 'all 0.15s',
                                                }}
                                                    onMouseOver={e => { e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.background = 'rgba(239,68,68,0.08)'; }}
                                                    onMouseOut={e => { e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.background = 'none'; }}
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {stats.productBreakdown.length === 0 && manualExpenses.length === 0 && (
                                    <tr><td colSpan="5" style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                                        <Receipt size={32} style={{ opacity: 0.2, marginBottom: '8px' }} /><br />Henüz gider kaydı yok
                                    </td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            <style>{`@media (max-width: 1024px) { .expense-grid { grid-template-columns: 1fr !important; } }`}</style>
        </div>
    );
};

/* ===== PRODUCTS TAB ===== */
const ProductsTab = ({ products, localCosts, setLocalCosts, onSave, saving }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [simId, setSimId] = useState(products[0]?.id || '');
    const [disc, setDisc] = useState(10);

    const sel = useMemo(() => products.find(p => p.id === simId), [products, simId]);
    const cost = sel ? (localCosts[sel.id] || 0) : 0;
    const currentProf = sel ? (sel.numericPrice - cost) : 0;
    const simProf = sel ? (sel.numericPrice * (1 - disc / 100)) - cost : 0;

    const filteredProducts = useMemo(() => {
        if (!searchTerm) return products;
        const q = searchTerm.toLowerCase();
        return products.filter(p => p.name.toLowerCase().includes(q) || p.category.toLowerCase().includes(q));
    }, [products, searchTerm]);

    const avgMargin = useMemo(() => {
        const prods = products.filter(p => p.numericPrice > 0);
        if (prods.length === 0) return 0;
        const totalMargin = prods.reduce((acc, p) => {
            const c = localCosts[p.id] || 0;
            return acc + ((p.numericPrice - c) / p.numericPrice) * 100;
        }, 0);
        return Math.round(totalMargin / prods.length);
    }, [products, localCosts]);

    return (
        <div className="fade-in responsive-accounting-grid" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem', alignItems: 'start' }}>
            <div className="card glass" style={{ padding: 0, overflow: 'hidden' }}>
                {/* Table header */}
                <div style={{
                    padding: '1rem 1.5rem', borderBottom: '1px solid var(--border)',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <h3 style={{ fontWeight: 700, fontSize: '0.95rem', margin: 0 }}>Ürün Maliyet Cetveli</h3>
                        <span style={{
                            padding: '3px 10px', borderRadius: '8px', fontSize: '0.72rem',
                            fontWeight: 700, background: avgMargin > 50 ? '#f0fdf4' : avgMargin > 20 ? '#fffbeb' : '#fef2f2',
                            color: avgMargin > 50 ? '#22c55e' : avgMargin > 20 ? '#f59e0b' : '#ef4444',
                        }}>Ort. Marj: %{avgMargin}</span>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <div style={{ position: 'relative' }}>
                            <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                            <input
                                placeholder="Ürün ara..."
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                style={{
                                    paddingLeft: '32px', width: '160px', height: '34px',
                                    background: 'var(--bg-main)', border: '1px solid var(--border)',
                                    borderRadius: '8px', fontSize: '0.8rem', outline: 'none',
                                    color: 'var(--text-main)',
                                }}
                            />
                        </div>
                        <button onClick={onSave} disabled={saving} className="btn-primary" style={{
                            padding: '7px 16px', borderRadius: '8px', fontSize: '0.8rem', fontWeight: 700,
                            opacity: saving ? 0.6 : 1,
                        }}>
                            {saving ? '...' : 'Kaydet'}
                        </button>
                    </div>
                </div>
                <div style={{ maxHeight: '600px', overflowY: 'auto', overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '580px' }}>
                        <thead style={{ position: 'sticky', top: 0, background: 'var(--bg-card)', zIndex: 10 }}>
                            <tr style={{ borderBottom: '2px solid var(--border)' }}>
                                <th style={thStyle}>Ürün</th>
                                <th style={{ ...thStyle, textAlign: 'center', width: '110px' }}>Maliyet (₺)</th>
                                <th style={{ ...thStyle, textAlign: 'center' }}>Satış Fiyatı</th>
                                <th style={{ ...thStyle, textAlign: 'center' }}>Kâr</th>
                                <th style={{ ...thStyle, textAlign: 'center', width: '120px' }}>Marj</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredProducts.map(p => {
                                const pCost = localCosts[p.id] || 0;
                                const pProfit = p.numericPrice - pCost;
                                const pMargin = p.numericPrice > 0 ? Math.floor((pProfit / p.numericPrice) * 100) : 0;
                                const marginColor = pMargin > 50 ? '#22c55e' : pMargin > 20 ? '#f59e0b' : '#ef4444';
                                return (
                                    <tr key={p.id} style={{ borderBottom: '1px solid var(--border)' }}>
                                        <td style={{ padding: '0.75rem 1.5rem' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                {p.image ? (
                                                    <img src={p.image} style={{ width: '32px', height: '32px', borderRadius: '8px', objectFit: 'cover' }} alt={p.name} />
                                                ) : (
                                                    <div style={{ width: '32px', height: '32px', background: 'rgba(var(--primary-rgb), 0.08)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <Package size={14} color="var(--primary)" />
                                                    </div>
                                                )}
                                                <div>
                                                    <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{p.name}</div>
                                                    <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>{p.category}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td style={{ padding: '0.75rem 1rem', textAlign: 'center' }}>
                                            <input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={pCost}
                                                onChange={e => setLocalCosts({ ...localCosts, [p.id]: parseFloat(e.target.value) || 0 })}
                                                style={{
                                                    width: '90px', height: '32px', padding: '0 8px', textAlign: 'center',
                                                    borderRadius: '8px', border: '2px solid var(--border)',
                                                    background: 'var(--bg-main)', color: 'var(--text-main)',
                                                    fontWeight: 700, fontSize: '0.85rem', outline: 'none',
                                                }}
                                                onFocus={e => e.currentTarget.style.borderColor = 'var(--primary)'}
                                                onBlur={e => e.currentTarget.style.borderColor = 'var(--border)'}
                                            />
                                        </td>
                                        <td style={{ padding: '0.75rem 1rem', textAlign: 'center', fontWeight: 600, fontSize: '0.85rem' }}>{p.numericPrice} ₺</td>
                                        <td style={{ padding: '0.75rem 1rem', textAlign: 'center', fontWeight: 700, color: pProfit > 0 ? '#22c55e' : '#ef4444', fontSize: '0.85rem' }}>{pProfit.toLocaleString('tr-TR')} ₺</td>
                                        <td style={{ padding: '0.75rem 1rem' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                                                <div style={{ flex: 1, height: '6px', background: 'var(--bg-main)', borderRadius: '3px', overflow: 'hidden', maxWidth: '60px' }}>
                                                    <div style={{ width: `${Math.min(100, Math.max(0, pMargin))}%`, height: '100%', background: marginColor, borderRadius: '3px', transition: 'width 0.3s' }} />
                                                </div>
                                                <span style={{ fontSize: '0.75rem', fontWeight: 700, color: marginColor, minWidth: '32px' }}>%{pMargin}</span>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Simulator */}
            <div className="card glass simulator-card" style={{
                background: 'var(--bg-card)', border: '2px solid var(--primary)',
                padding: '1.5rem', position: 'sticky', top: '2rem', borderRadius: '20px',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '1.5rem', color: 'var(--primary)' }}>
                    <Calculator size={22} />
                    <h3 style={{ fontWeight: 800, fontSize: '1rem', margin: 0 }}>İndirim Simülatörü</h3>
                </div>

                <div style={{ marginBottom: '1.25rem' }}>
                    <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 700, marginBottom: '6px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Ürün</label>
                    <select value={simId} onChange={e => setSimId(e.target.value)} style={{
                        width: '100%', height: '40px', borderRadius: '10px',
                        background: 'var(--bg-main)', color: 'var(--text-main)',
                        border: '1px solid var(--border)', fontSize: '0.85rem', fontWeight: 600,
                        padding: '0 12px', outline: 'none',
                    }}>
                        {products.map(p => <option key={p.id} value={p.id}>{p.name} ({p.numericPrice} ₺)</option>)}
                    </select>
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <label style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>İndirim Oranı</label>
                        <b style={{ color: 'var(--primary)', fontSize: '0.9rem' }}>%{disc}</b>
                    </div>
                    <input type="range" min="0" max="50" step="5" value={disc} onChange={e => setDisc(parseInt(e.target.value))} style={{ width: '100%', accentColor: 'var(--primary)' }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.65rem', color: 'var(--text-muted)', marginTop: '4px' }}>
                        <span>%0</span><span>%50</span>
                    </div>
                </div>

                {sel && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        <div style={{
                            padding: '1rem', borderRadius: '12px', background: 'var(--bg-main)',
                            border: '1px solid var(--border)',
                        }}>
                            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontWeight: 600, marginBottom: '4px' }}>Mevcut Kâr</div>
                            <div style={{ fontSize: '1.25rem', fontWeight: 800 }}>{currentProf.toLocaleString('tr-TR')} ₺</div>
                        </div>
                        <div style={{
                            padding: '1rem', borderRadius: '12px',
                            background: simProf > 0 ? 'rgba(34, 197, 94, 0.06)' : 'rgba(239, 68, 68, 0.06)',
                            border: `2px solid ${simProf > 0 ? '#22c55e' : '#ef4444'}`,
                        }}>
                            <div style={{ fontSize: '0.7rem', color: simProf > 0 ? '#22c55e' : '#ef4444', fontWeight: 700, marginBottom: '4px' }}>
                                %{disc} İndirimli Kâr
                            </div>
                            <div style={{ fontSize: '1.5rem', fontWeight: 900, color: simProf > 0 ? '#22c55e' : '#ef4444' }}>
                                {simProf.toLocaleString('tr-TR', { maximumFractionDigits: 2 })} ₺
                            </div>
                            {simProf <= 0 && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.7rem', color: '#ef4444', fontWeight: 700, marginTop: '4px' }}>
                                    <AlertTriangle size={12} /> Zarar ediliyor!
                                </div>
                            )}
                        </div>
                        <div style={{
                            padding: '10px 12px', background: 'rgba(14, 165, 233, 0.06)',
                            borderRadius: '10px', display: 'flex', gap: '8px', alignItems: 'flex-start',
                        }}>
                            <Info size={14} color="#0ea5e9" style={{ marginTop: '2px', flexShrink: 0 }} />
                            <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)', margin: 0 }}>
                                Kârdaki değişim: <b style={{ color: currentProf > 0 && (currentProf - simProf) > 0 ? '#ef4444' : '#22c55e' }}>
                                    {currentProf > 0 ? `%${Math.floor(((currentProf - simProf) / currentProf) * 100)}` : '-'} {currentProf > simProf ? 'azalma' : 'artış'}
                                </b>
                            </p>
                        </div>
                    </div>
                )}
            </div>
            <style>{`@media (max-width: 1024px) { .responsive-accounting-grid { grid-template-columns: 1fr !important; } .simulator-card { position: static !important; } }`}</style>
        </div>
    );
};

/* ===== METRIC CARD ===== */
const MetricCard = ({ title, value, icon, color, trend, sub }) => (
    <div className="card glass" style={{ padding: '1.25rem', position: 'relative', overflow: 'hidden' }}>
        <div style={{
            position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: color,
        }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
            <span style={{ color: 'var(--text-muted)', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{title}</span>
            <div style={{
                width: '36px', height: '36px', borderRadius: '10px',
                background: `${color}15`, color: color,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
                {icon}
            </div>
        </div>
        <div style={{ fontSize: '1.5rem', fontWeight: 800, lineHeight: 1.2 }}>{value}</div>
        {sub && <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '4px' }}>{sub}</div>}
        {trend === 'up' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px' }}>
                <ArrowUpRight size={12} style={{ color: '#22c55e' }} />
                <span style={{ fontSize: '0.7rem', color: '#22c55e', fontWeight: 600 }}>Gelir aktif</span>
            </div>
        )}
    </div>
);

/* ===== SHARED STYLES ===== */
const thStyle = {
    padding: '0.75rem 1.5rem', textAlign: 'left',
    fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase',
    letterSpacing: '0.05em', color: 'var(--text-muted)',
};

const paginationBtnStyle = {
    padding: '6px 10px', borderRadius: '8px',
    border: '1px solid var(--border)', background: 'var(--bg-card)',
    cursor: 'pointer', display: 'flex', alignItems: 'center',
    color: 'var(--text-main)',
};

export default Accounting;
